import { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import api, { fileUrl } from "@/lib/api";
import Layout from "@/components/Layout";
import StatusBadge from "@/components/StatusBadge";
import ShipmentMap from "@/components/ShipmentMap";
import { Button } from "@/components/ui/button";
import { ArrowLeft, MapPin, Path, Package, QrCode, ClockCountdown, Leaf, Truck, CheckCircle } from "@phosphor-icons/react";
import { toast } from "sonner";

const FLOW = ["created", "dispatched", "in_transit", "delivered"];

export default function ShipmentDetail() {
  const { id } = useParams();
  const [s, setS] = useState(null);
  const [qr, setQr] = useState(null);
  const navigate = useNavigate();

  const load = () => api.get(`/shipments/${id}`).then((r) => setS(r.data));
  useEffect(() => {
    load();
    api.get(`/shipments/${id}/qr`).then((r) => setQr(r.data)).catch(() => {});
  }, [id]); // eslint-disable-line

  const advance = async (status) => {
    try {
      await api.post(`/shipments/${id}/status`, { status });
      toast.success(`Status → ${status.replace("_", " ")}`);
      load();
    } catch { toast.error("Could not update status"); }
  };

  if (!s) return <Layout title="Shipment"><div className="text-slate-400">Loading…</div></Layout>;

  const idx = FLOW.indexOf(s.status);

  return (
    <Layout title={s.code} subtitle={`${s.batch_no} · ${s.product}`}
      actions={<Button variant="outline" data-testid="back-btn" onClick={() => navigate("/shipments")}><ArrowLeft size={16} className="mr-1.5" /> Back</Button>}>
      {/* progress pipeline */}
      <div className="bg-white border border-slate-200 rounded-md p-5 mb-6">
        <div className="flex items-center justify-between mb-3">
          <StatusBadge status={s.status} />
          <div className="flex gap-2">
            {s.status === "created" && <Button size="sm" data-testid="advance-status" className="bg-yellow-500 hover:bg-yellow-600 text-white" onClick={() => advance("dispatched")}>Mark Dispatched</Button>}
            {s.status !== "delivered" && s.status !== "created" && <Button size="sm" data-testid="advance-status" className="bg-blue-600 hover:bg-blue-700" onClick={() => advance("in_transit")}>Mark In Transit</Button>}
          </div>
        </div>
        <div className="flex items-center gap-1" data-testid="status-pipeline">
          {FLOW.map((f, i) => (
            <div key={f} className="flex-1 flex items-center gap-1">
              <div className={`h-1.5 flex-1 rounded-full ${i <= idx ? "bg-slate-900" : "bg-slate-200"}`} />
            </div>
          ))}
        </div>
        <div className="flex justify-between mt-2 text-[11px] uppercase tracking-wider font-semibold text-slate-400">
          {FLOW.map((f, i) => <span key={f} className={i <= idx ? "text-slate-900" : ""}>{f.replace("_", " ")}</span>)}
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2 space-y-6">
          {/* metrics */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <Metric icon={Path} label="Distance" value={`${s.distance_km} km`} />
            <Metric icon={ClockCountdown} label="Rules-based ETA" value={`${s.eta_hours} h`} accent="#2563EB" />
            <Metric icon={Leaf} label="CO₂ Estimate" value={`${s.co2_kg} kg`} accent="#059669" />
            <Metric icon={Package} label="Cargo" value={`${s.quantity_tons} t`} />
          </div>

          {/* map */}
          <div className="bg-white border border-slate-200 rounded-md overflow-hidden">
            <div className="px-5 py-3 border-b border-slate-200 flex items-center justify-between">
              <h3 className="font-heading font-semibold">Route & Last Known Location</h3>
              {s.last_location
                ? <span className="text-xs text-slate-500 flex items-center gap-1.5"><span className="h-2 w-2 rounded-full bg-blue-500 live-dot" />Updated {new Date(s.last_location.ts).toLocaleTimeString()}</span>
                : <span className="text-xs text-slate-400">No GPS pings yet</span>}
            </div>
            <div className="h-[360px]"><ShipmentMap focus={s} /></div>
            <div className="px-5 py-2 text-[11px] text-slate-400 border-t border-slate-100">
              GPS is intermittent (browser-limited). We show the last known position + timestamp.
            </div>
          </div>

          {/* checkpoints */}
          <div className="bg-white border border-slate-200 rounded-md">
            <div className="px-5 py-3 border-b border-slate-200"><h3 className="font-heading font-semibold">Checkpoints ({s.checkpoints.length}/{s.planned_checkpoints})</h3></div>
            <div className="p-5 space-y-3">
              {s.checkpoints.length === 0 && <div className="text-sm text-slate-400">No checkpoints logged yet.</div>}
              {s.checkpoints.map((c) => (
                <div key={c.id} className="flex items-start gap-3" data-testid={`cp-${c.id}`}>
                  <div className="mt-0.5 h-6 w-6 rounded-full bg-green-100 text-green-700 flex items-center justify-center"><CheckCircle size={14} weight="fill" /></div>
                  <div>
                    <div className="text-sm font-medium text-slate-900">{c.label}</div>
                    <div className="text-xs text-slate-400">{c.by} · {new Date(c.ts).toLocaleString()}</div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* side */}
        <div className="space-y-6">
          <div className="bg-white border border-slate-200 rounded-md p-5 text-center">
            <div className="text-xs uppercase tracking-wider font-bold text-slate-500 flex items-center justify-center gap-1.5 mb-3"><QrCode size={14} /> Digital ID QR</div>
            {qr ? <img data-testid="shipment-qr" src={qr.qr} alt="QR" className="mx-auto w-44 h-44" /> : <div className="h-44" />}
            <div className="font-mono text-sm font-semibold mt-2">{s.code}</div>
            <div className="text-xs text-slate-400 mt-1">Receiver scans to confirm delivery</div>
          </div>

          <div className="bg-white border border-slate-200 rounded-md p-5 space-y-3">
            <Row icon={MapPin} label="Origin" value={s.origin.name} />
            <Row icon={MapPin} label="Destination" value={s.destination.name} />
            <Row icon={Truck} label="Vehicle" value={`${s.vehicle?.reg_number || "—"} · ${s.vehicle?.vehicle_type_label || ""}`} />
            <Row icon={Package} label="Driver" value={s.driver_name} />
            <Row icon={Package} label="Receiver" value={s.receiver_name || "Unassigned (open scan)"} />
            {s.delivered_at && <Row icon={CheckCircle} label="Delivered" value={`${new Date(s.delivered_at).toLocaleString()}${s.received_by ? ` · ${s.received_by}` : ""}`} />}
          </div>

          <Button asChild variant="outline" className="w-full" data-testid="download-cert">
            <a href={fileUrl("/carbon/export.pdf")}>Download ESG Report (PDF)</a>
          </Button>
        </div>
      </div>
    </Layout>
  );
}

function Metric({ icon: Icon, label, value, accent = "#0F172A" }) {
  return (
    <div className="bg-white border border-slate-200 rounded-md p-4">
      <div className="flex items-center gap-1.5 text-xs uppercase tracking-wider font-bold text-slate-500"><Icon size={14} style={{ color: accent }} />{label}</div>
      <div className="font-mono text-xl font-medium mt-1.5 text-slate-900">{value}</div>
    </div>
  );
}

function Row({ icon: Icon, label, value }) {
  return (
    <div className="flex items-start gap-3">
      <Icon size={16} className="text-slate-400 mt-0.5 shrink-0" />
      <div className="min-w-0">
        <div className="text-[11px] uppercase tracking-wider font-bold text-slate-400">{label}</div>
        <div className="text-sm text-slate-800 truncate">{value}</div>
      </div>
    </div>
  );
}
